import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Image as ImageIcon, Upload, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_THEME = { join: "#0A84FF", settings: "#12466F", leave: "#EF4444" };

/**
 * Editor dialog shared by the Community list (create) and the CommunityDetail
 * page (edit).
 *   - The banner (bg_image_url) can be set via URL OR by uploading a local
 *     file when the community already exists (upload endpoint needs a cid).
 */
const CommunityEditorDialog = ({ open, onOpenChange, community, onSaved }) => {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [bg, setBg] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (community) {
      setName(community.name || "");
      setDesc(community.description || "");
      setBg(community.bg_image_url || "");
      setIsPublic(community.is_public !== false);
      setTheme(community.theme || DEFAULT_THEME);
    } else {
      setName(""); setDesc(""); setBg(""); setIsPublic(true); setTheme(DEFAULT_THEME);
    }
  }, [community, open]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Nome obbligatorio"); return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), description: desc, bg_image_url: bg, is_public: isPublic, theme };
      const r = community
        ? await api.put(`/communities/${community.id}`, payload)
        : await api.post("/communities", payload);
      toast.success(community ? "Community aggiornata" : "Community creata");
      onSaved && onSaved(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!community?.id) {
      toast.error("Salva prima la community, poi carica la copertina");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 5 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/communities/${community.id}/banner-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBg(r.data.url);
      toast.success("Copertina caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il caricamento");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="community-editor">
        <DialogHeader>
          <DialogTitle>{community ? "Impostazioni community" : "Nuova community"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-slate-500">Nome *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl mt-1 bg-slate-50" data-testid="community-name" />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-widest text-slate-500">Descrizione</Label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={3}
              className="w-full rounded-xl mt-1 bg-slate-50 border border-slate-200 px-3 py-2 text-sm resize-none"
              placeholder="Di cosa si occupa la tua community?"
              data-testid="community-desc"
            />
          </div>

          {/* Banner section --------------------------------------------- */}
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-slate-500">Copertina</Label>

            {/* Preview */}
            {bg && (
              <div className="mt-1.5 relative rounded-xl overflow-hidden h-32 border border-slate-200" data-testid="community-bg-preview">
                <img src={bg} alt="Banner" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setBg("")}
                  className="absolute top-2 right-2 rounded-full bg-white/90 hover:bg-white p-1.5 shadow"
                  title="Rimuovi copertina"
                  data-testid="community-bg-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <div className="relative mt-2">
              <ImageIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={bg}
                onChange={(e) => setBg(e.target.value)}
                placeholder="https://immagine.esempio/foto.jpg"
                className="rounded-xl pl-9 bg-slate-50"
                data-testid="community-bg-url"
              />
            </div>

            <div className="mt-2 flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onPickFile}
                disabled={uploading || !community?.id}
                className="rounded-full text-xs"
                title={!community?.id ? "Prima crea la community, poi carica la copertina" : ""}
                data-testid="community-bg-upload-btn"
              >
                {uploading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Carico…</> : <><Upload className="w-3.5 h-3.5 mr-1.5" /> Carica da locale</>}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={onFile}
                className="hidden"
                data-testid="community-bg-file"
              />
              <span className="text-[10px] text-slate-500">PNG/JPG/WEBP · max 5 MB</span>
            </div>
            {!community?.id && (
              <p className="text-[10px] text-slate-500 mt-1">
                Suggerimento: crea la community con il pulsante qui sotto, poi apri le impostazioni per caricare una copertina.
              </p>
            )}
          </div>

          {/* Theme colours ---------------------------------------------- */}
          <div className="grid grid-cols-3 gap-2">
            {[["join", "Partecipa"], ["settings", "Impostazioni"], ["leave", "Abbandona"]].map(([k, lbl]) => (
              <div key={k}>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">{lbl}</Label>
                <div className="flex items-center gap-2 mt-1 rounded-xl bg-slate-50 border border-slate-200 px-2 py-1.5">
                  <input
                    type="color"
                    value={theme[k]}
                    onChange={(e) => setTheme((t) => ({ ...t, [k]: e.target.value }))}
                    className="w-8 h-8 rounded cursor-pointer"
                    data-testid={`community-color-${k}`}
                  />
                  <span className="font-mono-num text-xs text-slate-600 truncate">{theme[k]}</span>
                </div>
              </div>
            ))}
          </div>

          <label className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
            <div>
              <div className="text-sm font-medium">Pubblica</div>
              <div className="text-[11px] text-slate-500">Chiunque può entrare direttamente senza approvazione.</div>
            </div>
            <Switch checked={isPublic} onCheckedChange={setIsPublic} data-testid="community-public" />
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-slate-900 text-white" data-testid="community-save">
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Salvo…</> : (community ? "Salva" : "Crea community")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CommunityEditorDialog;
